import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  currentYearMonth,
  type YearMonth,
} from "@/app/routes/dashboard/content-calendar/calendar.utils";

/** Month, Week, List or Schedule (DESIGN.md "The Calendar in Parts"). "grid" is the old name for Month. */
export type CalendarLayoutMode = "month" | "week" | "list" | "schedule";
/** A phone's own views: List (the default), Month and Schedule. */
export type PhoneCalendarLayout = "list" | "month" | "schedule";

interface CalendarViewStore extends YearMonth {
  layoutMode: CalendarLayoutMode;
  setView: (view: YearMonth | ((prev: YearMonth) => YearMonth)) => void;
  setLayoutMode: (mode: CalendarLayoutMode) => void;
  phoneLayout: PhoneCalendarLayout;
  setPhoneLayout: (layout: PhoneCalendarLayout) => void;
}

const CALENDAR_VIEW_STORAGE_KEY = "calendar-view-store";

/**
 * Persists the dashboard Content Calendar month the user last looked at
 * across reloads and navigations away from the calendar, plus the view it was in.
 */
export const useCalendarViewStore = create<CalendarViewStore>()(
  persist(
    (set, get) => ({
      ...currentYearMonth(),
      layoutMode: "month",
      setView: (view) => {
        const next = typeof view === "function" ? view({ year: get().year, month: get().month }) : view;
        set({ year: next.year, month: next.month });
      },
      setLayoutMode: (layoutMode) => set({ layoutMode }),
      phoneLayout: "list",
      setPhoneLayout: (phoneLayout) => set({ phoneLayout }),
    }),
    {
      name: CALENDAR_VIEW_STORAGE_KEY,
      partialize: (state) => ({
        year: state.year,
        month: state.month,
        layoutMode: state.layoutMode,
        phoneLayout: state.phoneLayout,
      }),
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<CalendarViewStore>;
        const stored = p.layoutMode as string | undefined;
        return {
          ...current,
          ...p,
          layoutMode: stored === "list" || stored === "week" || stored === "schedule" ? stored : "month",
          phoneLayout: p.phoneLayout === "month" || p.phoneLayout === "schedule" ? p.phoneLayout : "list",
        };
      },
    },
  ),
);
